import React, { useState, useEffect, useContext } from "react";
import {
  Table,
  Space,
  Button,
  Typography,
  Layout,
  message,
  Input,
  Select,
  Tag,
  Modal,
  Popconfirm,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  SearchOutlined,
  DeleteOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { BASE_URL, couponTypeOptions } from "../../Constants";
import AppContext from "../../AppContext";
import { useTranslation } from "react-i18next";

const { Content } = Layout;
const { Title } = Typography;
const { Option } = Select;

function CouponsList() {
  const { t } = useTranslation();
  const [coupons, setCoupons] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [filteredCoupons, setFilteredCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState("all");
  const [plans, setPlans] = useState([]);
  const navigate = useNavigate();
  const { profile } = useContext(AppContext);

  useEffect(() => {
    const filtered = coupons.filter((coupon) => {
      const matchesType =
        typeFilter === "all" || coupon.type === typeFilter.toLowerCase();
      const matchesSearch =
        coupon.name.toLowerCase().includes(searchText.toLowerCase()) ||
        coupon.code.toLowerCase().includes(searchText.toLowerCase());
      return matchesType && matchesSearch;
    });
    setFilteredCoupons(filtered);
  }, [typeFilter, searchText]);

  useEffect(() => {
    fetchCoupons();
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/Lookup/plans`);
      setPlans(response.data || []);
    } catch (error) {
      console.error("Error fetching plans:", error);
    }
  };

  const fetchCoupons = async () => {
    try {
      const response = await axios.get(`${BASE_URL}/Coupon`);
      setCoupons(response.data);
      setFilteredCoupons(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching coupons:", error);
      message.error(t("messages.error.fetchCoupons"));
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      // Simulate API call
      const response = await axios.delete(`${BASE_URL}/Coupon/${id}`);
      if (response.status === 200) {
        setCoupons(coupons.filter((coupon) => coupon.id !== id));
        setFilteredCoupons(
          filteredCoupons.filter((coupon) => coupon.id !== id)
        );
        message.success("Coupon deleted successfully");
      }
    } catch (error) {
      message.error("Failed to delete coupon");
    }
  };

  const toggleStatus = async (id) => {
    try {
      const response = await axios.patch(
        `${BASE_URL}/Coupon/${id}/toggle-status`
      );
      if (response.status === 200) {
        setCoupons((prevCoupons) =>
          prevCoupons.map((coupon) =>
            coupon.id === id ? response.data : coupon
          )
        );
        setFilteredCoupons((prevFilteredCoupons) =>
          prevFilteredCoupons.map((coupon) =>
            coupon.id === id ? response.data : coupon
          )
        );
        message.success("Coupon status updated successfully");
      }
    } catch (error) {
      message.error("Failed to update coupon status");
    }
  };

  const getStatusTag = (coupon) => {
    if (!coupon.isActive) {
      return <Tag color="default">Inactive</Tag>;
    }
    if (new Date(coupon.expirationDate) < new Date()) {
      return <Tag color="red">Expired</Tag>;
    }
    if (coupon.currentUses >= coupon.maxUses) {
      return <Tag color="orange">Limit Reached</Tag>;
    }
    return <Tag color="green">Active</Tag>;
  };

  const getTypeTag = (type) => {
    return type === "coupon" ? (
      <Tag color="blue">Trial Coupon</Tag>
    ) : (
      <Tag color="purple">Discount Code</Tag>
    );
  };

  const getDiscountDisplay = (coupon) => {
    if (coupon.type === "coupon") {
      return `${coupon.trialDurationDays} days trial`;
    }
    if (coupon.discountType === "percentage") {
      return `${coupon.discountValue}%`;
    }
    return `${coupon.discountValue} SAR`;
  };

  const getTargetingDisplay = (coupon) => {
    const products = coupon.applicableProducts || [];
    if (products.length === 0) {
      return <Tag>All Products</Tag>;
    }
    return (
      <Space direction="vertical" size={2}>
        {products.map((product) => (
          <Tag key={product} color="geekblue">
            {product === "digital-card" ? "Digital Card" : product}
          </Tag>
        ))}
        {products.includes("digital-card") && (
          <span className="text-xs text-gray-500">
            Packages:{" "}
            {coupon.applicablePlanIds && coupon.applicablePlanIds.length > 0
              ? coupon.applicablePlanIds
                  .map(
                    (planId) =>
                      plans.find((plan) => plan.id === planId)?.name || planId
                  )
                  .join(", ")
              : "All Digital Card Plans"}
          </span>
        )}
      </Space>
    );
  };

  const columns = [
    {
      title: "Code",
      dataIndex: "code",
      key: "code",
      render: (text) => <span className="font-semibold">{text}</span>,
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      render: (type) => getTypeTag(type),
    },
    {
      title: "Value",
      key: "value",
      render: (_, record) => getDiscountDisplay(record),
    },
    {
      title: "Usage",
      key: "usage",
      render: (_, record) => (
        <span>
          {record.currentUses} / {record.maxUses}
        </span>
      ),
    },
    {
      title: "Targeting",
      key: "targeting",
      render: (_, record) => getTargetingDisplay(record),
    },
    {
      title: "Per User",
      key: "perUser",
      render: (_, record) =>
        record.maxUsesPerUser != null ? record.maxUsesPerUser : "Unlimited",
    },
    {
      title: "Expiration",
      dataIndex: "expirationDate",
      key: "expirationDate",
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: "Status",
      key: "status",
      render: (_, record) => getStatusTag(record),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space size="small">
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={() => navigate(`/coupon-details/${record.id}`)}
          />
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => navigate(`/coupon-form/${record.id}`)}
          />
          <Button
            size="small"
            onClick={() => toggleStatus(record.id)}
            type={record.isActive ? "default" : "primary"}
          >
            {record.isActive ? "Deactivate" : "Activate"}
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this coupon?"
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button icon={<DeleteOutlined />} size="small" danger />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Layout className="min-h-full bg-transparent">
      <header className="py-4 bg-transparent">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold tracking-tight text-white">
            Coupons & Discount Codes
          </h1>
        </div>
      </header>
      <Content className="rounded-lg px-5 py-6 shadow bg-white sm:px-6">
        <div className="flex justify-between items-center mb-6">
          {/* <Title level={2}>Coupons & Discount Codes</Title> */}
        </div>

        <div className="flex gap-4 mb-4 justify-between">
          <div className="flex gap-4">
            <Input
              placeholder="Search by code or name..."
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 300 }}
            />
            <Select
              placeholder="Filter by type"
              value={typeFilter}
              onChange={setTypeFilter}
              style={{ width: 200 }}
            >
              <Option value="all">All Types</Option>
              {couponTypeOptions.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          </div>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate("/coupon-form")}
          >
            Add New Coupon
          </Button>
        </div>

        <Table
          columns={columns}
          dataSource={filteredCoupons}
          rowKey="id"
          i18nIsDynamicList
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} items`,
          }}
        />
      </Content>
    </Layout>
  );
}

export default CouponsList;
